import { useEffect, useState } from "react";
import { Heart, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="#25d366" className="w-4 h-4">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const COPY = {
  ru: {
    badge: "Подарок к букету",
    title: "Мишки в подарок",
    subtitle: "Любимый человек обнимет своего мишку — а потом обнимет вас. Идеальное дополнение к букету.",
    cta: "Заказать",
    altSuffix: "плюшевый мишка в подарок к букету",
  },
  en: {
    badge: "Gift with bouquet",
    title: "Plush Bears",
    subtitle: "Your loved one will hug their bear — and then hug you. The perfect addition to a bouquet.",
    cta: "Order",
    altSuffix: "plush bear gift with a bouquet",
  },
  tr: {
    badge: "Bukete hediye",
    title: "Pelus Ayicik",
    subtitle: "Sevdiginiz kisi ayicigina sarilacak — ve sonra size sarilacak. Bukete mukemmel ek.",
    cta: "Siparis Et",
    altSuffix: "bukete hediye pelus ayicik",
  },
};

export default function BearsSection({ citySlug, cityName, lang = "ru", onOrderClick, currencySymbol = "$", convertPrice }) {
  const [bears, setBears] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    axios
      .get(`/api/bears?city=${citySlug}`)
      .then((r) => {
        if (!cancel) setBears(r.data || []);
      })
      .catch(() => {})
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
  }, [citySlug]);

  const t = COPY[lang] || COPY.ru;

  if (loading) return null;
  if (!bears || bears.length === 0) return null;

  return (
    <section
      className="py-12 sm:py-20 bg-gradient-to-b from-white via-rose-50/40 to-white"
      data-testid="bears-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-100 to-rose-100 px-4 py-1.5 rounded-full mb-4 border border-amber-200/50">
            <Gift className="w-4 h-4 text-amber-700" />
            <span className="text-xs sm:text-sm font-semibold text-amber-800 uppercase tracking-wide">
              {t.badge}
            </span>
          </div>
          <h2
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            {t.title}
          </h2>
          <p className="text-gray-600 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
            {t.subtitle}
          </p>
        </div>

        {/* Bears grid — 3 cards always in a row */}
        <div className="grid grid-cols-3 gap-3 sm:gap-6 lg:gap-8">
          {bears.map((bear) => {
            const displayName =
              lang === "tr" && bear.name_tr
                ? bear.name_tr
                : lang === "en" && bear.name_en
                ? bear.name_en
                : bear.name;
            const displayDesc =
              lang === "tr" && bear.description_tr
                ? bear.description_tr
                : lang === "en" && bear.description_en
                ? bear.description_en
                : bear.description;
            const price = convertPrice ? convertPrice(bear.price) : bear.price;

            return (
              <div
                key={bear.id}
                className="group bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-md sm:shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
                data-testid={`bear-card-${bear.id}`}
              >
                <div className="aspect-square relative overflow-hidden bg-gradient-to-br from-rose-50 to-amber-50">
                  <img
                    src={bear.image}
                    alt={`${displayName} — ${t.altSuffix} (${cityName})`}
                    width={400}
                    height={400}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-1.5 right-1.5 sm:top-3 sm:right-3 bg-white/95 backdrop-blur-sm px-1.5 py-0.5 sm:px-3 sm:py-1 rounded-full shadow-md flex items-center gap-1">
                    <Heart className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-rose-500 fill-rose-500" />
                    <span className="text-[10px] sm:text-xs font-bold text-gray-900">
                      {currencySymbol}
                      {price}
                    </span>
                  </div>
                </div>

                <div className="p-2.5 sm:p-5 lg:p-6">
                  <h3
                    className="text-sm sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2 truncate"
                    style={{ fontFamily: "Playfair Display, serif" }}
                    data-testid={`bear-name-${bear.id}`}
                  >
                    {displayName}
                  </h3>
                  <p className="hidden sm:block text-sm text-gray-600 leading-relaxed mb-5 min-h-[40px]">
                    {displayDesc}
                  </p>
                  <Button
                    onClick={() => onOrderClick(bear)}
                    className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white text-xs sm:text-base font-semibold rounded-full shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-1 sm:gap-2 h-8 sm:h-10 px-2"
                    data-testid={`bear-order-btn-${bear.id}`}
                  >
                    <WhatsAppIcon />
                    <span>{t.cta}</span>
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
